import { useState, type JSX } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ExternalLink, Loader2, RotateCcw, Undo2, X } from "lucide-react";
import { BROWSER_RESIZE_MAX_EDGE_PX } from "@ckgo2/shared";
import { mediaDetailHref } from "@/hooks/useMediaLibrary";
import { formatFileSize } from "@/lib/mediaFormat";
import {
  batchFileErrorText,
  batchFileView,
  type BatchPanelMode,
  type BatchPanelState,
} from "./BatchUploadPanel.model";
import type { BatchFile, BatchFileStatus, BatchUpload } from "./batchUpload.types";

/** Pět skupin stavů souboru: čeká, kontroluje nebo běží, hotovo, duplicita, chyba. */
const TONE_WAITING = "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
const TONE_WORKING = "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";
const TONE_DONE = "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300";
const TONE_DUPLICATE = "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300";
const TONE_FAILED = "bg-error-50 text-error-700 dark:bg-error-900/20 dark:text-error-400";

/**
 * Pohled na stav souboru: barva štítku, točící se ikona (`busy`) a text chyby (`failed`).
 * `Record` hlídá úplnost: nový stav v Kontraktu bez řádku tady padne v typechecku.
 */
const STATUS_VIEW: Record<BatchFileStatus, { tone: string; busy: boolean; failed: boolean }> = {
  queued: { tone: TONE_WAITING, busy: false, failed: false },
  hashing: { tone: TONE_WAITING, busy: true, failed: false },
  checking: { tone: TONE_WAITING, busy: true, failed: false },
  preparing: { tone: TONE_WORKING, busy: true, failed: false },
  uploading: { tone: TONE_WORKING, busy: true, failed: false },
  done: { tone: TONE_DONE, busy: false, failed: false },
  "duplicate-live": { tone: TONE_DUPLICATE, busy: false, failed: false },
  "duplicate-trash": { tone: TONE_DUPLICATE, busy: false, failed: false },
  "duplicate-in-batch": { tone: TONE_DUPLICATE, busy: false, failed: false },
  error: { tone: TONE_FAILED, busy: false, failed: true },
  "error-permanent": { tone: TONE_FAILED, busy: false, failed: true },
};

/**
 * Řádek souboru v panelu dávky: jméno, velikost, štítek stavu, text chyby a akce podle
 * `batchFileView`. Akce jen volají model dávky; stav souboru po nich mění model.
 */
export default function BatchFileRow({
  file,
  mode,
  state,
  batch,
}: {
  file: BatchFile;
  mode: BatchPanelMode;
  state: BatchPanelState;
  batch: Pick<BatchUpload, "remove" | "retry" | "restoreFromTrash">;
}): JSX.Element {
  const { t } = useTranslation();
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  // Lokální Record z literálních t(): typované t() dynamický klíč nebere a nový stav
  // v kontraktu bez štítku padne v typechecku.
  const statusLabels: Record<BatchFileStatus, string> = {
    queued: t("mediaUpload.status.queued"),
    hashing: t("mediaUpload.status.checking"),
    checking: t("mediaUpload.status.checking"),
    preparing: t("mediaUpload.status.preparing"),
    uploading: t("mediaUpload.status.uploading"),
    done: t("mediaUpload.status.done"),
    "duplicate-live": t("mediaUpload.status.duplicateLive"),
    "duplicate-trash": t("mediaUpload.status.duplicateTrash"),
    "duplicate-in-batch": t("mediaUpload.status.duplicateInBatch"),
    error: t("mediaUpload.status.error"),
    "error-permanent": t("mediaUpload.status.errorPermanent"),
  };

  const view = batchFileView(file, mode, state);
  const { tone, busy, failed } = STATUS_VIEW[file.status];

  const restore = () => {
    setRestoring(true);
    setRestoreError(null);
    batch
      .restoreFromTrash(file.key)
      .catch((err: unknown) =>
        setRestoreError(
          err instanceof Error && err.message ? err.message : t("mediaUpload.errorGeneric"),
        ),
      )
      .finally(() => setRestoring(false));
  };

  return (
    <li
      data-batch-file=""
      data-name={file.name}
      data-status={file.status}
      data-media-id={file.mediaId}
      className="flex flex-col gap-1 px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {file.name}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatFileSize(file.size)}
        </span>
        {file.resized && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t("mediaUpload.resized", { px: BROWSER_RESIZE_MAX_EDGE_PX })}
          </span>
        )}
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
        >
          {busy && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
          {statusLabels[file.status]}
        </span>
        {view.inGallery && (
          <span className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded-full px-2 py-0.5 text-xs font-medium">
            {t("mediaUpload.inGallery")}
          </span>
        )}
        {view.existingMediaId && (
          <Link
            to={mediaDetailHref(view.existingMediaId)}
            target="_blank"
            rel="noopener noreferrer"
            data-batch-existing-link=""
            className="text-primary-600 dark:text-primary-400 inline-flex items-center gap-1 text-xs font-medium hover:underline"
          >
            {t("mediaUpload.openExisting")}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </Link>
        )}
        {view.restore && (
          <button
            type="button"
            data-batch-restore=""
            disabled={restoring}
            onClick={restore}
            className="btn-secondary btn-sm"
          >
            {restoring ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {t("mediaUpload.restore")}
          </button>
        )}
        {view.retry && (
          <button
            type="button"
            data-batch-retry=""
            onClick={() => batch.retry(file.key)}
            className="btn-secondary btn-sm"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            {t("mediaUpload.retry")}
          </button>
        )}
        {view.remove && (
          <button
            type="button"
            aria-label={t("mediaUpload.remove", { name: file.name })}
            title={t("mediaUpload.remove", { name: file.name })}
            onClick={() => batch.remove(file.key)}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
      {failed && (
        <p className="text-error-700 dark:text-error-400 text-xs">{batchFileErrorText(file)}</p>
      )}
      {restoreError && (
        <p className="text-error-700 dark:text-error-400 text-xs" role="alert">
          {restoreError}
        </p>
      )}
    </li>
  );
}
